import { DropDownList } from '@progress/kendo-react-dropdowns';
import { type FC, type ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'src/components/design-system/Button';
import ClearFilterButton from 'src/components/design-system/ClearFilterButton';
import PopUp from 'src/components/design-system/PopUp';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import {
  SHOPPING_LIST_FILTER_OPTIONS,
  SHOPPING_LIST_SORT_OPTIONS,
} from 'src/contexts/viewShoppingListsConstants';
import useConfirm from 'src/hooks/useConfirm';
import { useContextSelector } from 'use-context-selector';
import { ViewShoppingListsContext } from './ViewShoppingListsContext';

const ViewShoppingListsGridHeader: FC = () => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();

  const selectedFilter = useContextSelector(ViewShoppingListsContext, (s) => s.selectedFilter);
  const handleSelectedFilterChange = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.handleSelectedFilterChange
  );
  const selectedSort = useContextSelector(ViewShoppingListsContext, (s) => s.selectedSort);
  const setSelectedSort = useContextSelector(ViewShoppingListsContext, (s) => s.setSelectedSort);
  const hasActiveFilters = useContextSelector(ViewShoppingListsContext, (s) => s.hasActiveFilters);
  const clearFilters = useContextSelector(ViewShoppingListsContext, (s) => s.clearFilters);
  const shoppingLists = useContextSelector(ViewShoppingListsContext, (s) => s.shoppingLists);
  const selectedShoppingListIds = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.selectedShoppingListIds
  );
  const hasSelectedShoppingLists = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.hasSelectedShoppingLists
  );
  const canShareShoppingLists = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.canShareShoppingLists
  );
  const canCopyShoppingLists = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.canCopyShoppingLists
  );
  const openCreateShoppingListPanel = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.openCreateShoppingListPanel
  );
  const openCopyShoppingListPanel = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.openCopyShoppingListPanel
  );
  const openShareShoppingListPanel = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.openShareShoppingListPanel
  );
  const deleteSelectedShoppingLists = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.deleteSelectedShoppingLists
  );
  const isDeletingShoppingList = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.isDeletingShoppingList
  );

  const filterOptions = useMemo(
    () => SHOPPING_LIST_FILTER_OPTIONS.map((option) => ({ ...option, label: t(option.label) })),
    [t]
  );

  const sortOptions = useMemo(
    () => SHOPPING_LIST_SORT_OPTIONS.map((option) => ({ ...option, label: t(option.label) })),
    [t]
  );

  const selectedFilterOption = useMemo(
    () =>
      filterOptions.find((option) => option.value === selectedFilter.value) ?? filterOptions[0],
    [filterOptions, selectedFilter.value]
  );

  const selectedSortOption = useMemo(
    () => sortOptions.find((option) => option.value === selectedSort.value) ?? sortOptions[0],
    [selectedSort.value, sortOptions]
  );

  const selectedLists = useMemo(
    () => shoppingLists.filter((list) => selectedShoppingListIds.includes(list.id)),
    [selectedShoppingListIds, shoppingLists]
  );
  const selectedOwnedLists = useMemo(
    () => selectedLists.filter((list) => !list.isShare),
    [selectedLists]
  );
  const ineligibleSharedListNames = useMemo(
    () => selectedLists.filter((list) => list.isShare).map((list) => list.title),
    [selectedLists]
  );
  const hasIneligibleSharedLists = ineligibleSharedListNames.length > 0;

  const canEnableCopy = canCopyShoppingLists && hasSelectedShoppingLists;
  const canEnableShare = canShareShoppingLists && hasSelectedShoppingLists;
  const canEnableDelete =
    hasSelectedShoppingLists && !hasIneligibleSharedLists && !isDeletingShoppingList;

  const handleDeleteClick = useCallback(async () => {
    if (!canEnableDelete) {
      return;
    }

    if (selectedOwnedLists.length === 0) {
      return;
    }

    const isSingleDelete = selectedOwnedLists.length === 1;
    const deleteMessage = isSingleDelete
      ? t('Are you sure you want to delete {{listName}}?', {
          listName: selectedOwnedLists[0].title,
        })
      : t('Are you sure you want to delete selected lists?');
    const isDeleteAccepted = await confirm({
      title: t('deleteConfirmationTitle'),
      content: <p className="break-words">{deleteMessage}</p>,
      confirmButtonText: t('yesDelete'),
      cancelButtonText: t('noCancel'),
      width: '420px',
    });

    if (!isDeleteAccepted) {
      return;
    }

    try {
      await deleteSelectedShoppingLists();
    } catch {
      toast.error(t('Failed to delete shopping list'));
    }
  }, [canEnableDelete, confirm, deleteSelectedShoppingLists, selectedOwnedLists, t]);

  const renderBulkActionTooltip = (
    actionLabel: string,
    selectionRequiredKey: string,
    treatLockedAsIneligible = false
  ): ReactNode => {
    if (!hasSelectedShoppingLists) {
      return (
        <div className="m-1 max-w-[350px]">
          <PopUpContentWrapper type="Information" contents={[t(selectionRequiredKey)]} />
        </div>
      );
    }

    if (treatLockedAsIneligible && hasIneligibleSharedLists) {
      return (
        <div className="max-h-[60vh] overflow-auto">
          <PopUpContentWrapper
            type="Error"
            prefix={actionLabel}
            title={t('Lists below are ineligible to {{action}}', { action: actionLabel })}
            contents={ineligibleSharedListNames}
          />
        </div>
      );
    }

    return null;
  };

  const copyActionLabel = t('Send a Copy');
  const shareActionLabel = t('Share');
  const deleteActionLabel = t('Delete');

  return (
    <div className="flex w-full flex-row flex-wrap items-center justify-between gap-x-2 gap-y-2 bg-[#233843] px-2 py-1">
      <div className="flex w-full min-w-0 flex-row flex-wrap items-center gap-x-2 gap-y-2">
        <div className="flex min-w-0 flex-row flex-wrap items-center gap-2">
          <span className="[&_button]:!m-0 [&_button:focus-visible]:!outline [&_button:focus-visible]:!outline-2 [&_button:focus-visible]:!outline-offset-2 [&_button:focus-visible]:!outline-[#1a73e8]">
            <ClearFilterButton onClick={clearFilters} disabled={!hasActiveFilters} />
          </span>
          <Button
            color="primary"
            size="lg"
            className="!m-0"
            onClick={openCreateShoppingListPanel}
          >
            {t('Create New List')}
          </Button>
          {canCopyShoppingLists && (
            <PopUp
              popupAlign={{ horizontal: 'center', vertical: 'top' }}
              anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
              className="max-h-[180px]"
              content={renderBulkActionTooltip(copyActionLabel, 'Please select list(s) to Copy.')}
              show={!canEnableCopy}
            >
              <Button
                color="info"
                size="lg"
                className="!m-0"
                disabled={!canEnableCopy}
                onClick={() => openCopyShoppingListPanel()}
              >
                {copyActionLabel}
              </Button>
            </PopUp>
          )}
          {canShareShoppingLists && (
            <PopUp
              popupAlign={{ horizontal: 'center', vertical: 'top' }}
              anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
              className="max-h-[180px]"
              content={renderBulkActionTooltip(shareActionLabel, 'Please select list(s) to Share.')}
              show={!canEnableShare}
            >
              <Button
                color="info"
                size="lg"
                className="!m-0"
                disabled={!canEnableShare}
                onClick={() => openShareShoppingListPanel()}
              >
                {shareActionLabel}
              </Button>
            </PopUp>
          )}
          <PopUp
            popupAlign={{ horizontal: 'center', vertical: 'top' }}
            anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
            className="max-h-[180px]"
            content={renderBulkActionTooltip(
              deleteActionLabel,
              'Please select list(s) to Delete.',
              true
            )}
            show={!canEnableDelete}
          >
            <Button
              color="error"
              size="lg"
              className="!m-0"
              disabled={!canEnableDelete}
              onClick={() => {
                void handleDeleteClick();
              }}
            >
              {deleteActionLabel}
            </Button>
          </PopUp>
        </div>

        <div className="ml-auto flex min-w-0 flex-row flex-wrap items-center justify-end gap-2">
          <div className="min-w-0" style={{ width: 220 }}>
            <DropDownList
              id="view-shopping-lists-filter-dropdown"
              className="k-picker-solid product-filter-dropdown"
              data={filterOptions}
              textField="label"
              dataItemKey="value"
              value={selectedFilterOption}
              onChange={(e) => handleSelectedFilterChange(e.value)}
              style={{ width: 220 }}
              popupSettings={{ className: 'toolbar-dropdown-popup' }}
            />
          </div>
          <div className="flex min-w-0 flex-shrink-0 items-center gap-2">
            <label
              htmlFor="view-shopping-lists-sort-dropdown"
              className="whitespace-nowrap text-sm text-white"
            >
              {t('Sort by')}
            </label>
            <DropDownList
              id="view-shopping-lists-sort-dropdown"
              className="k-picker-solid product-filter-dropdown"
              style={{ width: 200 }}
              data={sortOptions}
              textField="label"
              dataItemKey="value"
              value={selectedSortOption}
              onChange={(e) => setSelectedSort(e.value)}
              popupSettings={{ className: 'toolbar-dropdown-popup' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewShoppingListsGridHeader;
